<!-- A dropdown to select Tag objects to filter a list by. -->

<template>
  <v-menu
    v-model="showMenu"
    offset-y
    bottom
    right
    lazy
    :close-on-content-click="false"
    :disabled="disabled"
  >
    <v-badge
      slot="activator"
      overlap
      left
      :class="{smaller: selectedTagIds.length > 99}"
    >
      <span
        v-if="showBadge && selectedTagIds.length > 0"
        slot="badge"
      >
        {{ selectedTagIds.length }}
      </span>
      <v-btn
        icon
        class="ma-0"
      >
        <v-icon
          :class="{'grey--text text--darken-1': !active, 'msr--text': active}"
        >
          tag
        </v-icon>
      </v-btn>
    </v-badge>
    <v-list class="pa-0">
      <!-- Reset Button -->
      <v-list-tile
        v-if="tags.length > 0"
        class="no-padding"
      >
        <v-btn
          block
          flat
          class="pl-0 grey--text"
          @click="reset()"
        >
          <v-layout align-center>
            <v-icon class="ml-3 mr-2">
              clear
            </v-icon>
            {{ $t('clearFilters') }}
          </v-layout>
        </v-btn>
      </v-list-tile>
      <v-divider />
      <!-- Filter -->
      <v-list-tile
        v-if="tags.length > 5"
        class="list-tile-auto-height pb-1"
      >
        <v-text-field
          v-model="filterText"
          :placeholder="$t('filterTags')"
          hide-details
          class="show-messages pa-0"
          prepend-icon="search"
          clearable
          @click.stop
          @keyup.native.esc="filterText = ''"
        />
      </v-list-tile>
    </v-list>
    <v-list style="max-height: 300px; overflow: auto">
      <!-- Empty -->
      <v-list-tile
        v-if="tags.length === 0"
        class="grey--text"
      >
        {{ $t('tagsEmpty') }}
      </v-list-tile>
      <!-- Tag Checkboxes -->
      <v-list-tile
        v-for="tag in filteredTags"
        :key="tag.id"
        class="list-tile-auto-height"
        style="height: 32px"
      >
        <v-layout align-center>
          <v-checkbox
            :input-value="isTagIdSelected(tag.id)"
            hide-details
            class="mt-0"
            color="msr"
            @change="onTagIdToggled(tag.id, $event)"
          >
            <div slot="label">
              <v-chip
                :color="getTagColor(tag)"
                class="ml-0 clickable"
                small
              >
                <span :style="{color: getTagTextColor(tag)}">{{
                  tag.name
                }}</span>
              </v-chip>
            </div>
          </v-checkbox>
        </v-layout>
      </v-list-tile>
    </v-list>
    <!-- Go to Tag Page Button -->
    <v-list
      v-if="showTagsManagementLink"
      class="pb-0"
    >
      <v-divider />
      <v-list-tile
        v-if="!hasNoUploadGroups"
        :title="$t('tagsManagement')"
        @click="$router.push({name: 'tagsmanage'})"
      >
        <v-icon class="mr-1 grey--text">
          shortcut
        </v-icon>
        {{ $t('tagsManagement') }}
      </v-list-tile>
    </v-list>
  </v-menu>
</template>

<script>
import tagsMixin from '@/mixins/tags';

export default {
  mixins: [tagsMixin],
  props: {
    tags: {
      type: Array[Object],
      required: true,
    },
    /** Add v-model support. Use from outside like: v-model="..." */
    value: {
      type: Array[Number],
      required: true,
    },
    active: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    showTagsManagementLink: {
      type: Boolean,
      default: true,
    },
    showBadge: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    const vm = this;
    return {
      busy: false,
      showMenu: false,
      filterText: '',
    };
  },
  computed: {
    hasNoUploadGroups() {
      return this.$store.getters.uploadgroups.length === 0;
    },
    /** Implement the v-model handlers. */
    selectedTagIds: {
      get() {
        return this.value;
      },
      set(ids) {
        this.$emit('input', ids);
      },
    },
    filteredTags() {
      if (!this.filterText) {
        return this.tags;
      }
      return this.tags.filter((tag) => {
        const name = tag.name.toLowerCase();
        const search = this.filterText.toLowerCase();
        return name.indexOf(search) !== -1;
      });
    },
  },
  watch: {
    showMenu: function (show) {
      if (!show) {
        // When menu is closed, wait for animation and reset filter.
        setTimeout(() => this.resetFilterText(), 300);
      }
    },
  },
  methods: {
    isTagIdSelected(id) {
      return this.selectedTagIds.indexOf(id) !== -1;
    },
    /**
     * Update selected category ids list in store as needed.
     */
    onTagIdToggled(id, selected) {
      if (selected) {
        // Add id to list.
        if (this.isTagIdSelected(id)) {
          return;
        }

        const newIds = this.selectedTagIds.slice();
        newIds.push(id);
        this.selectedTagIds = newIds;
      } else {
        // Remove id from list.
        const index = this.selectedTagIds.indexOf(id);
        const notContained = index === -1;
        if (notContained) {
          return;
        }

        const newIds = this.selectedTagIds.slice();
        newIds.splice(index, 1);
        this.selectedTagIds = newIds;
      }
    },
    reset() {
      this.selectedTagIds = [];
      this.resetFilterText();
    },
    resetFilterText() {
      this.filterText = '';
    },
  },
};
</script>

<style scoped>
.v-menu__content >>> .no-scrollbar {
  overflow-y: hidden;
}

.no-padding >>> .v-list__tile {
  padding: 0;
}

.v-list .v-input .v-messages {
  display: none;
}

/** Default for v-text-field inside a v-list seems to hide messages. */
.show-messages >>> .v-text-field__details .v-messages {
  display: initial;
}
</style>



// WEBPACK FOOTER //
// src/components/TagFilterMenu.vue